import { ChevronDown, ChevronRight, File as FileIcon, Folder, FolderOpen } from 'lucide-react';

import { cn } from '../lib/cn.js';
import type { FileTreeNode } from '../lib/file-tree-builder.js';

export interface FileTreeNodeViewProps {
  readonly node: FileTreeNode;
  readonly depth: number;
  readonly expanded: ReadonlySet<string>;
  readonly selectedNodeId: string | null;
  readonly onToggle: (id: string) => void;
  readonly onSelect: (id: string) => void;
  readonly isVisible: (n: FileTreeNode) => boolean;
}

export function FileTreeNodeView({
  node,
  depth,
  expanded,
  selectedNodeId,
  onToggle,
  onSelect,
  isVisible,
}: FileTreeNodeViewProps): JSX.Element | null {
  if (!isVisible(node)) return null;

  const isFolder = node.kind === 'Directory';
  const isOpen = expanded.has(node.id);
  const isSelected = node.id === selectedNodeId;

  const handleClick = (): void => {
    if (isFolder) onToggle(node.id);
    else onSelect(node.id);
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={handleClick}
        title={node.path}
        className={cn(
          'group flex w-full items-center gap-1.5 truncate border-l-2 px-2 py-1 text-left font-mono text-[12px] transition-colors',
          isSelected
            ? 'border-accent bg-accent/15 text-text-primary'
            : 'border-transparent text-text-secondary hover:bg-hover hover:text-text-primary',
        )}
        style={{ paddingLeft: 8 + depth * 14 }}
      >
        <span className="flex h-3.5 w-3.5 flex-none items-center justify-center text-text-muted">
          {isFolder ? (
            isOpen ? (
              <ChevronDown className="h-3 w-3" />
            ) : (
              <ChevronRight className="h-3 w-3" />
            )
          ) : null}
        </span>
        <span className="flex h-4 w-4 flex-none items-center justify-center">
          {isFolder ? (
            isOpen ? (
              <FolderOpen className="h-3.5 w-3.5 text-node-folder" />
            ) : (
              <Folder className="h-3.5 w-3.5 text-node-folder" />
            )
          ) : (
            <FileIcon className="h-3.5 w-3.5 text-node-file" />
          )}
        </span>
        <span className="truncate">{node.name}</span>
      </button>
      {isFolder && isOpen && node.children.length > 0 && (
        <div>
          {node.children.map((child) => (
            <FileTreeNodeView
              key={child.id}
              node={child}
              depth={depth + 1}
              expanded={expanded}
              selectedNodeId={selectedNodeId}
              onToggle={onToggle}
              onSelect={onSelect}
              isVisible={isVisible}
            />
          ))}
        </div>
      )}
    </div>
  );
}
